// @flow
/**
 * A component to wrap/render backbone views. It will instantiate the backbone
 * view with options as the main parameters.
 */
import React, { Component } from 'react';

import typeof JQuery from 'jquery';

/**
 * Added this check to support the different navigations possible using sidebars
 * and the search functionality within the Transactions, Income Statement and
 * Balance Sheet pages. The wrapper should rerender based on if and which sidebar
 * is open, and if it is displaying search results.
 */
const shouldSkipRenderBasedOnSidebar = (props, prevProps) => {
  const navigatingToBasePage = props.pathParams.length === 0;
  if (navigatingToBasePage) {
    return false;
  }

  const navigatingFromSidebar =
    prevProps.pathParams.includes('transaction') || prevProps.pathParams.includes('overview');
  const navigatingToSidebar =
    props.pathParams.includes('transaction') || props.pathParams.includes('overview');
  const isNewSearch = props.queryVals.searchkey !== prevProps.queryVals.searchkey;
  const isCurrentlySearchPage = props.queryVals.searchkey || props.queryVals.returl;

  if (!navigatingFromSidebar && navigatingToSidebar) {
    return true;
  }
  if (navigatingFromSidebar && !navigatingToSidebar && !(isCurrentlySearchPage && isNewSearch)) {
    return true;
  }
  if (navigatingFromSidebar && navigatingToSidebar) {
    return true;
  }
  if (!navigatingFromSidebar && !navigatingToSidebar && isCurrentlySearchPage && !isNewSearch) {
    return true;
  }
  return false;
};

export interface BackboneBaseView {
  constructor(Object, Array<string>): void;
  render(): void;
  remove(): void;
  unbind(): void;
  $el: $Call<JQuery>;
}
type BackboneWrapperProps = {
  /**
   * The Backbone BaseView instance to render.
   */
  view: Class<BackboneBaseView>,
  /**
   * Parameters needed to instantiate the view
   */
  pathParams: Array<string>,
  /**
   * Parsed query string variables for the view
   */
  queryVals: { [string]: string },
  /**
   * A marker value that changes whenever a backbone view is to be reloaded. Note that this is
   * different from refreshing which simply re-renders the view. Reloading will dismount and
   * re-initialize the view (as if it was reloaded on a new page). This mechanism is used to
   * implement `BackboneRouter.refreshPage()`.
   */
  reloadMarker: number,
};

export default class BackboneWrapper extends Component<BackboneWrapperProps> {
  static defaultProps = {
    reloadMarker: 0,
  };

  componentDidMount() {
    this.createView();
  }

  componentDidUpdate(prevProps: BackboneWrapperProps) {
    if (this.props.view !== prevProps.view) {
      this.reloadView();
      return;
    }

    if (shouldSkipRenderBasedOnSidebar(this.props, prevProps)) {
      return;
    }

    const reloadMarkerUpdated = prevProps.reloadMarker !== this.props.reloadMarker;
    const unchangedParams =
      prevProps.pathParams.length === this.props.pathParams.length &&
      prevProps.pathParams.every(
        (value, index) => this.props.pathParams[index].toString() === value.toString(),
      );

    if (reloadMarkerUpdated || !unchangedParams) {
      this.reloadView();
    }
  }

  componentWillUnmount() {
    this.cleanupView();
  }

  instance: ?BackboneBaseView = null;

  createView() {
    const { view, pathParams, queryVals } = this.props;

    // eslint-disable-next-line
    this.instance = new view({ queryVals }, pathParams);
  }

  cleanupView(leaveElement: boolean = false) {
    const { instance } = this;
    if (!instance) {
      return;
    }

    instance.remove();
    instance.unbind();
    if (!leaveElement) {
      instance.$el.empty();
    }
    this.instance = null;
  }

  reloadView() {
    this.cleanupView(true);
    this.createView();
  }

  render() {
    return (
      <div>
        <div className="backbone-container widget">
          {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
          <h1>LOADING</h1>
        </div>
      </div>
    );
  }
}
